import { useMemo, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  V2AppShell,
  V2Sidebar,
  V2Topbar,
  V2BottomNav,
  V2Button,
  V2EmptyState,
  V2Alert,
  type V2NavItem,
  type V2NavGroup,
  type V2BottomNavItem,
  useTheme,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import logoUrl from '@sakhtar/design-system/assets/logo/sakhtar-white-logo.svg';
import { useApiGet } from '../lib/useApiGet.js';
import { tenantBasePath } from '../lib/tenant.js';
import type { ShellContextResponse } from '../lib/types/shell.js';
import {
  BRAND_COLORS,
  SIDEBAR_SKINS,
  type BrandColor,
  type OwnProfileResponse,
  type SidebarSkin,
  type ThemeMode,
} from '../lib/types/membership.js';
import { YarMark } from './yar/YarMark.js';
import { DESKTOP_NAV_GROUPS, MOBILE_NAV, isNavEntryEntitled, type NavEntry } from './navConfig.js';
import { canSeeNavEntry } from './navAccess.js';
import './pageEnter.css';
import { TopbarPopovers } from './topbar/TopbarPopovers.js';
import { computeBreadcrumb } from './breadcrumb.js';
import {
  FirstLoginProfileFlow,
  type CompletedProfile,
} from '../routes/profile/FirstLoginProfileFlow.js';
import { refreshSession } from '../lib/auth.js';
import { UserAvatar } from '../lib/avatars.js';
import type { ShellOutletContext } from './shellOutletContext.js';
import { DemoBanner } from './yar/DemoBanner.js';
import { YarAccessProvider } from './yar/YarAccess.js';
import { YarFab } from './yar/YarFab.js';
import { MobileMenuDrawer } from './MobileMenuDrawer.js';
import { useKeyboardOpen } from './useKeyboardOpen.js';
import { TenantSettingsProvider } from '../lib/tenantSettings.js';
import { CapabilitiesProvider, evaluateCapabilities } from '../lib/capabilities.js';
import { PageHelpButton, PageHelpProvider } from '../help/PageHelp.js';

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP - Final.dc.html — sidebar (grouped IA) + topbar
 *   (glass, breadcrumb, global product layer) + mobile bottom nav
 * canonical pattern: V2AppShell (design-system v2) — grouped desktop
 *   sidebar + glass topbar, mobile bottom utility bar under 900px
 * state reference: ready · permission_denied · network_error (shell-context
 *   phase)
 * tablet (900–1279px): Sakhtar Cloud ERP v2.dc.html — icon-rail sidebar +
 *   burger-opened overlay drawer. The <900px mobile shell is unchanged.
 *
 * Wraps every authenticated route. Fetches shell-context once per mount
 * (persists across nested navigation — this component doesn't remount on
 * route changes within /t/:slug/**). Entitlements gate nav *visibility*
 * only; every linked route still enforces its own permission check.
 *
 * The sidebar's top strip is only the real Sakhtar platform mark (same
 * asset as LoginPage's brand surface) — the tenant-identity row that used
 * to sit under it (building icon + tenant name + "فضای کاری سازمان") was
 * removed per explicit product feedback. The topbar breadcrumb is a page
 * location trail ("خانه / فروش و CRM / سرنخ‌ها"), not route metadata, so
 * the tenant slug does not belong in it; the slug is still visible via the
 * topbar's user-menu popover (`userRole={orgLabel}` below), so no identity
 * is actually lost. There is no per-tenant display name in the API yet (see
 * the topbar userName comment below), so `orgLabel` stays the tenant slug.
 */
function ThemeToggle() {
  const { mode, setMode } = useTheme();
  return (
    <V2Button variant="ghost" size="sm" onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}>
      {mode === 'dark' ? 'حالت روشن' : 'حالت تاریک'}
    </V2Button>
  );
}

interface StoredAppearance {
  readonly brandColor: BrandColor | null;
  readonly sidebarSkin: SidebarSkin | null;
}

function appearanceStorageKey(slug: string | undefined): string | null {
  return slug ? `sakhtar.appearance:${slug}` : null;
}

/** Last saved brand/sidebar for this workspace; unknown or unreadable values read as absent. */
function readStoredAppearance(slug: string | undefined): StoredAppearance | null {
  const key = appearanceStorageKey(slug);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { brandColor?: unknown; sidebarSkin?: unknown };
    const brandColor = (BRAND_COLORS as readonly unknown[]).includes(parsed.brandColor)
      ? (parsed.brandColor as BrandColor)
      : null;
    const sidebarSkin = (SIDEBAR_SKINS as readonly unknown[]).includes(parsed.sidebarSkin)
      ? (parsed.sidebarSkin as SidebarSkin)
      : null;
    return { brandColor, sidebarSkin };
  } catch {
    return null;
  }
}

function writeStoredAppearance(
  slug: string | undefined,
  brandColor: BrandColor | null,
  sidebarSkin: SidebarSkin | null,
): void {
  const key = appearanceStorageKey(slug);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify({ brandColor, sidebarSkin }));
  } catch {
    // Private mode / blocked storage: the flash fix degrades, nothing breaks.
  }
}

export function AuthenticatedShell() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Same context ThemeToggle() reads/writes above — calling useTheme() a
  // second time here is safe (it's just a context read); this gives the
  // shell its own handle on setMode so a personal themeMode pick in
  // AccountPage/FirstLoginProfileFlow can drive the one real `data-theme`
  // directly instead of fighting ThemeToggle for it (see
  // applyThemeModePreview below).
  const { setMode } = useTheme();
  // See the session-heartbeat effect below for why a retry exists at all —
  // bumping this forces both fetches below to run again after a session
  // refresh succeeds, instead of staying stuck on the stale 404 they failed
  // with the first time.
  const [retryKey, setRetryKey] = useState(0);
  const context = useApiGet<ShellContextResponse>(
    slug ? `${tenantBasePath(slug)}/api/v1/admin/shell-context` : null,
    [slug, retryKey],
  );
  const me = useApiGet<OwnProfileResponse>(slug ? `${tenantBasePath(slug)}/api/v1/me` : null, [
    slug,
    retryKey,
  ]);

  useEffect(() => {
    if (!slug) return;
    const reloadMe = () => me.reload();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') reloadMe();
    };
    window.addEventListener('focus', reloadMe);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', reloadMe);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [slug, me.reload]);
  // First Login Profile: `profileCompletedAt` is the server's gate. Once
  // this session finishes the flow, trust that outcome directly (including
  // the name the wizard itself just saved, for the topbar) rather than
  // waiting on a refetch of `me` — a full page load later re-reads the real
  // value anyway, so this is session-scoped, not a second source of truth.
  const [justCompletedProfile, setJustCompletedProfile] = useState<CompletedProfile | null>(null);
  // Live preview for the wizard's "ظاهر حساب شما" step — picking a swatch
  // there should repaint this shell's own data-brand/data-sidebar
  // immediately, not wait for the PATCH + a /me refetch that never even
  // happens on its own (see FirstLoginProfileFlow's onAppearancePreview
  // doc comment). Never reset back to null: by the time the wizard moves
  // past step 4 it has already PATCHed these same values, so the preview
  // and the real saved state agree from then on.
  const [appearancePreview, setAppearancePreview] = useState<{
    readonly brandColor: BrandColor;
    readonly sidebarSkin: SidebarSkin;
  } | null>(null);
  // Shared with every routed page via <Outlet context> — see
  // shellOutletContext.ts. setAppearancePreview's identity is stable across
  // renders (useState setter), so this object only needs to be memoized for
  // the same reason any context value does: a fresh object on every render
  // would otherwise make every consumer's own effects re-run needlessly.
  //
  // Reported live: picking a light/dark/system mode in AccountPage's or
  // FirstLoginProfileFlow's "ظاهر حساب" section and confirming didn't
  // actually change the app's rendered theme — only brandColor/sidebarSkin
  // did. Root cause: `data-theme` was only ever driven by ThemeToggle's own
  // `setMode`, never by the saved/previewed personal preference at all
  // (not even after a save + reload — `App.tsx`'s initial mode comes from
  // localStorage only). `onThemeModePreview` below is that same `setMode`,
  // so both entry points drive one real state instead of fighting over
  // `data-theme`. `'system'` is resolved once, at the moment a preview
  // fires, since ThemeProvider's own `mode` is a concrete dark/light value
  // with no live media-query subscription of its own.
  const shellOutletContext: ShellOutletContext = useMemo(
    () => ({
      onAppearancePreview: setAppearancePreview,
      onThemeModePreview: (next: ThemeMode) => {
        const resolved =
          next === 'system'
            ? window.matchMedia('(prefers-color-scheme: dark)').matches
              ? 'dark'
              : 'light'
            : next;
        setMode(resolved);
      },
    }),
    [setMode],
  );

  // The session cookie lives 15 minutes (packages/auth's issueSession) with
  // nothing on the client renewing it on its own — an active user's tab was
  // silently logged out mid-session, and because ADR-001 §2.3 deliberately
  // makes an expired-session 404 indistinguishable from a wrong tenant slug
  // (apps/api/src/tenant/resolve.ts), the shell then showed "فضای کاری یافت
  // نشد" (workspace not found) instead of ever re-authenticating. Two parts
  // fix this: a heartbeat refresh well inside the 15-minute window so an
  // active session never actually expires, and a one-shot fallback that
  // retries once via refreshSession before treating a 404 here as fatal.
  const [recovering, setRecovering] = useState(false);
  const recoveryAttempted = useRef(false);
  const lastRefreshAt = useRef(Date.now());

  // 900–1279px only (CSS-gated in V2AppShell): the sidebar is an icon rail
  // and the topbar burger expands it as an overlay drawer. Close it on
  // navigation and on Escape. The <900px mobile shell (bottom nav) never
  // renders the burger, so this state is inert there.
  const [navOpen, setNavOpen] = useState(false);
  // <900px: the bottom bar's «منو» opens a drawer over the page, not a routed screen.
  const [menuOpen, setMenuOpen] = useState(false);
  useKeyboardOpen();
  useEffect(() => {
    setNavOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  useEffect(() => {
    if (!slug) return;
    const interval = setInterval(
      () => {
        lastRefreshAt.current = Date.now();
        refreshSession(slug).catch(() => {
          // A heartbeat failure isn't fatal on its own — the reactive
          // fallback below (or the existing 401 handling) covers it once an
          // actual request fails.
        });
      },
      5 * 60 * 1000,
    );
    return () => clearInterval(interval);
  }, [slug]);

  // Timers are frozen while a tab sleeps (background throttling, laptop lid),
  // so also refresh as soon as the tab is visible again if the last refresh is
  // older than the heartbeat interval.
  useEffect(() => {
    if (!slug) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefreshAt.current < 4 * 60 * 1000) return;
      lastRefreshAt.current = Date.now();
      refreshSession(slug).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [slug]);

  const entitledModuleSlugs = useMemo(() => {
    if (context.status === 'ready' && context.data.phase === 'ready' && context.data.entitlements) {
      return context.data.entitlements.entitledModuleSlugs;
    }
    return [];
  }, [context]);
  const subscriptionMode =
    context.status === 'ready' && context.data.phase === 'ready'
      ? (context.data.subscription?.mode ?? null)
      : null;
  const isDemo = slug === 'demo' || slug === 'demo-preview';

  if (!slug) return null;

  const base = tenantBasePath(slug);

  const is401Error =
    context.status === 'error' &&
    (context.error.status === 401 || context.error.status === 403) &&
    context.error.code !== 'MEMBERSHIP_DISABLED';

  const isMembershipDisabled =
    (context.status === 'error' && context.error.code === 'MEMBERSHIP_DISABLED') ||
    (me.status === 'error' && me.error.code === 'MEMBERSHIP_DISABLED');

  const isTenantNotFoundError =
    context.status === 'error' &&
    (context.error.status === 404 || context.error.code === 'NOT_FOUND');

  // A plain 401 means the 15-minute access cookie lapsed (idle/sleeping tab);
  // the refresh cookie usually still holds, so refresh once before login.
  const isUnauthorized =
    context.status === 'error' &&
    context.error.status === 401 &&
    context.error.code !== 'MEMBERSHIP_DISABLED';

  useEffect(() => {
    if (isMembershipDisabled && slug) {
      try {
        sessionStorage.setItem('sakhtar.membershipDisabled', '1');
      } catch {
        // LoginPage also reads navigate state.
      }
      navigate(`${base}/login`, { replace: true, state: { membershipDisabled: true } });
      return;
    }
    if (is401Error && slug && !(isUnauthorized && !recoveryAttempted.current)) {
      navigate(`${base}/login`, { replace: true });
    }
  }, [isMembershipDisabled, is401Error, isUnauthorized, slug, base, navigate]);

  // One-shot recovery for the 404 case: try a session refresh before
  // accepting "this tenant doesn't exist" at face value. If the refresh
  // itself fails (or a retried request 404s again), there's genuinely
  // nothing left to recover — send the user to login, same as a real 401,
  // rather than the misleading "فضای کاری یافت نشد" panel.
  useEffect(() => {
    if (!(isTenantNotFoundError || isUnauthorized) || !slug) return;
    if (recoveryAttempted.current) {
      navigate(`${base}/login`, { replace: true });
      return;
    }
    recoveryAttempted.current = true;
    setRecovering(true);
    refreshSession(slug)
      .then(() => {
        setRetryKey((k) => k + 1);
      })
      .catch(() => {
        navigate(`${base}/login`, { replace: true });
      })
      .finally(() => {
        setRecovering(false);
      });
  }, [isTenantNotFoundError, isUnauthorized, slug, base, navigate]);

  // Prefix match, not exact match: a module's sidebar item must stay
  // highlighted on every subroute (e.g. "فروش و CRM" active on
  // /crm/leads, /crm/quotes/Q-1, ...), not only on the module's own
  // overview route. Ties (a subroute path could technically prefix-match
  // more than one entry, e.g. "/admin" vs "/admin/users") are broken by
  // longest-hrefSuffix-wins, so the more specific entry (users-access)
  // beats the coarser one (home) on /admin/users.
  const matchScore = (target: string): number => {
    if (location.pathname === target) return target.length;
    if (location.pathname.startsWith(`${target}/`)) return target.length;
    return -1;
  };
  const bestNavEntry = (entries: readonly NavEntry[]): NavEntry | undefined => {
    let best: NavEntry | undefined;
    let bestScore = -1;
    for (const entry of entries) {
      const score = matchScore(`${base}${entry.hrefSuffix}`);
      if (score > bestScore) {
        bestScore = score;
        best = entry;
      }
    }
    return bestScore >= 0 ? best : undefined;
  };

  const activeDesktopEntry = bestNavEntry(DESKTOP_NAV_GROUPS.flatMap((g) => g.items));
  const activeMobileEntry = bestNavEntry(MOBILE_NAV);
  const isActive = (entry: NavEntry) => entry === activeDesktopEntry;
  const isMobileActive = (entry: NavEntry) => entry === activeMobileEntry;

  const toNavItem = (entry: NavEntry): V2NavItem => ({
    id: entry.id,
    label: entry.label,
    icon: entry.icon,
    href: `${base}${entry.hrefSuffix}`,
    active: isActive(entry),
    count: entry.count,
    iconNode: entry.customIcon === 'ai-assistant' ? <YarMark size={24} /> : undefined,
  });

  // `me` re-fetches on window focus / tab visibility, and useApiGet drops
  // back to 'loading' while it does. Reading the profile straight from that
  // state blanked data-brand for the length of the request, so the shell
  // flashed the default blue and then snapped back to the user's colour.
  // Keep the last profile that loaded until the refetch lands.
  const lastReadyMe = useRef<OwnProfileResponse | null>(null);
  if (me.status === 'ready') lastReadyMe.current = me.data;

  // Sidebar visibility follows the user's own permission keys (navAccess.ts).
  // Built from the last loaded profile so a focus refetch never un-hides a tab.
  const navAccessContext = useMemo(() => {
    const caps = evaluateCapabilities(lastReadyMe.current);
    return { ready: caps.isReady, allowedKeys: caps.allowedKeys, roleKeys: caps.roleKeys };
  }, [me]);

  const sidebarGroups: V2NavGroup[] = DESKTOP_NAV_GROUPS.map((group) => ({
    label: group.label,
    items: group.items
      .filter(
        (e) => isNavEntryEntitled(e, entitledModuleSlugs) && canSeeNavEntry(e.id, navAccessContext),
      )
      .map(toNavItem),
  })).filter((group) => group.items.length > 0);

  // The design authority is explicit: exactly 5 items, always — a
  // not-entitled item is dimmed and non-interactive, never removed (a 4- or
  // 6-item bar is not an allowed layout). See the "DISABLED" state in
  // Sakhtar Mobile App.dc.html §FRAME 03.
  const bottomNavItems: V2BottomNavItem[] = MOBILE_NAV.map((entry) => ({
    id: entry.id,
    label: entry.label,
    icon: entry.icon,
    href: `${base}${entry.hrefSuffix}`,
    active: isMobileActive(entry),
    primary: entry.id === 'add',
    disabled:
      !isNavEntryEntitled(entry, entitledModuleSlugs) ||
      !canSeeNavEntry(entry.id, navAccessContext),
  }));

  const activeEntry = activeDesktopEntry;
  // shell-context carries entitlements only, not an organization display
  // label (that field lives on GET .../admin/home — see lib/types/shell.ts)
  // — the tenant slug is the one identity this layer actually has.
  const orgLabel = slug;
  // GET /api/v1/me (First Login Profile) is real now — the previous
  // hardcoded "کاربر سازمان" placeholder is gone. Falls back to it only
  // while /me is still loading or genuinely has no name on file. Prefers
  // `justCompletedProfile` (set by the wizard's own onDone) over the
  // pre-completion `me` fetch, so the topbar shows the name the user just
  // entered immediately, without waiting on a refetch.
  const nameSource = justCompletedProfile ?? lastReadyMe.current;
  const meName = nameSource
    ? [nameSource.firstName, nameSource.lastName].filter(Boolean).join(' ')
    : '';
  const userName = meName.trim() || 'کاربر سازمان';
  // Same precedence as the name: what the wizard just saved, else `me`.
  const avatarSource = nameSource
    ? {
        avatarPreset: nameSource.avatarPreset,
        avatarPhotoDataUrl: nameSource.avatarPhotoDataUrl,
      }
    : null;
  // Before the first /me of a page load there is no profile at all, so a
  // full reload painted the default colour first. The last saved appearance
  // for this workspace is remembered per browser and used only until /me
  // answers; the server value always wins and refreshes it.
  const [storedAppearance] = useState(() => readStoredAppearance(slug));
  const brandColor = nameSource?.brandColor ?? storedAppearance?.brandColor ?? undefined;
  const sidebarSkin = nameSource?.sidebarSkin ?? storedAppearance?.sidebarSkin ?? undefined;
  useEffect(() => {
    if (!nameSource) return;
    writeStoredAppearance(slug, nameSource.brandColor, nameSource.sidebarSkin);
  }, [slug, nameSource?.brandColor, nameSource?.sidebarSkin]);
  const profileIncomplete =
    !justCompletedProfile && me.status === 'ready' && me.data.profileCompletedAt === null;

  const phase =
    context.status === 'loading'
      ? 'loading'
      : context.status === 'error'
        ? 'network_error'
        : context.data.phase === 'ready'
          ? 'ready'
          : context.data.phase;

  if (phase === 'loading' || is401Error) {
    return <LoadingState />;
  }
  if (recovering) {
    return <LoadingState />;
  }
  if (isTenantNotFoundError && !recoveryAttempted.current) {
    // The recovery effect above hasn't run yet for this render (effects
    // fire after commit) — treat this single frame as loading too, so the
    // wrong panel never has a chance to paint before the retry starts.
    return <LoadingState />;
  }
  if (isTenantNotFoundError) {
    return (
      <div
        style={{
          height: '100dvh',
          display: 'grid',
          placeContent: 'center',
          background: 'var(--sk-bg-canvas)',
        }}
      >
        <StatePanel
          variant="not_found"
          title="فضای کاری یافت نشد"
          description={`فضای کاری با آدرس «${slug}» وجود ندارد.`}
          action={
            <V2Button variant="primary" onClick={() => navigate('/start/login')}>
              ورود به فضای کاری دیگر
            </V2Button>
          }
        />
      </div>
    );
  }
  if (phase !== 'ready') {
    return (
      <StatePanel
        variant={phase === 'permission_denied' ? 'permission_denied' : 'network_error'}
        title={phase === 'permission_denied' ? 'دسترسی ندارید' : 'مشکلی پیش آمد'}
        description={phase === 'network_error' ? 'دوباره تلاش کنید.' : undefined}
      />
    );
  }

  return (
    <TenantSettingsProvider slug={slug}>
      <CapabilitiesProvider profile={me.status === 'ready' ? me.data : null}>
        <YarAccessProvider value={{ slug, subscriptionMode }}>
          <div
            className={isDemo ? 'demo-shell' : undefined}
            data-brand={appearancePreview?.brandColor ?? brandColor}
            data-sidebar={appearancePreview?.sidebarSkin ?? sidebarSkin}
          >
            {isDemo ? <DemoBanner /> : null}
            <PageHelpProvider pathname={location.pathname} tenantSlug={slug}>
              <V2AppShell
                navOpen={navOpen}
                onNavClose={() => setNavOpen(false)}
                sidebar={
                  <V2Sidebar
                    brandLogoUrl={logoUrl}
                    brandName="ساختار"
                    groups={sidebarGroups}
                    renderLink={(item, content) => (
                      <Link key={item.id} to={item.href} style={{ textDecoration: 'none' }}>
                        {content}
                      </Link>
                    )}
                  />
                }
                topbar={
                  <V2Topbar
                    breadcrumb={computeBreadcrumb(location.pathname, base, activeEntry)}
                    mobileBrand={{ logoUrl, name: 'ساختار' }}
                    onMenu={() => setNavOpen((o) => !o)}
                    menuOpen={navOpen}
                    utilities={
                      <>
                        <PageHelpButton />
                        <span className="sk-topbar__actions">
                          <ThemeToggle />
                        </span>
                        <TopbarPopovers
                          slug={slug}
                          entitledModuleSlugs={entitledModuleSlugs}
                          userName={userName}
                          userRole={orgLabel}
                          avatar={avatarSource}
                        />
                      </>
                    }
                  />
                }
                bottomNav={
                  <V2BottomNav
                    items={bottomNavItems.map((item) =>
                      item.id === 'menu'
                        ? { ...item, active: menuOpen || item.active }
                        : item.id === 'account'
                          ? {
                              ...item,
                              avatar: (
                                <UserAvatar name={userName} source={avatarSource} size={26} />
                              ),
                            }
                          : item,
                    )}
                    renderLink={(item, content) =>
                      item.id === 'menu' ? (
                        <button
                          key={item.id}
                          type="button"
                          aria-haspopup="dialog"
                          aria-expanded={menuOpen}
                          onClick={() => setMenuOpen(true)}
                          style={{
                            all: 'unset',
                            display: 'contents',
                            cursor: 'pointer',
                          }}
                        >
                          {content}
                        </button>
                      ) : item.disabled ? (
                        <span key={item.id} style={{ textDecoration: 'none' }}>
                          {content}
                        </span>
                      ) : (
                        <Link key={item.id} to={item.href} style={{ textDecoration: 'none' }}>
                          {content}
                        </Link>
                      )
                    }
                  />
                }
              >
                {isDemo &&
                !/\/ai(\/|$)/.test(location.pathname.slice(tenantBasePath(slug).length)) ? (
                  <V2Alert tone="warning" title="محیط نمایشی">
                    داده‌های این فضای کاری نمونه هستند و نباید به‌عنوان داده مشتری واقعی استفاده
                    شوند.
                  </V2Alert>
                ) : null}
                {me.status === 'ready' && me.data.accessReviewRequired ? (
                  <V2EmptyState
                    icon="shield"
                    title="نیازمند تعیین دسترسی"
                    description="دسترسی شما به بخش‌های سازمان هنوز تعیین نشده است. لطفاً با مدیر سازمان تماس بگیرید."
                  />
                ) : (
                  <Outlet context={shellOutletContext} />
                )}
              </V2AppShell>
            </PageHelpProvider>
            {profileIncomplete && me.status === 'ready' ? (
              <FirstLoginProfileFlow
                slug={slug}
                profile={me.data}
                onDone={(completed) => setJustCompletedProfile(completed)}
                onAppearancePreview={setAppearancePreview}
                onThemeModePreview={shellOutletContext.onThemeModePreview}
              />
            ) : null}
            <MobileMenuDrawer slug={slug} open={menuOpen} onClose={() => setMenuOpen(false)} />
            <YarFab slug={slug} />
          </div>
        </YarAccessProvider>
      </CapabilitiesProvider>
    </TenantSettingsProvider>
  );
}
